// src/components/advisor/AdvisorClientDossiers.jsx
// §8bis — Dossiers clients : un dossier existe même SANS compte ARTKIV.
// Champs minimum (§8) : nom d'usage, type de client, email facultatif.
// L'invitation / le rattachement d'un compte viendront dans une étape suivante ;
// l'absence de compte est un état NORMAL du cycle commercial (pas une anomalie).
// i18n FR/EN/DE inline.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { AUDIT_ACTIONS } from "../../config/contentVisibility";
import { PERMISSION_CATEGORIES, mergeGrants, permissionLabel } from "../../config/dossierPermissions";
import { UserPlus, Building2, User, Users, Landmark, Home, Archive, FolderPlus, ChevronDown, Unlink, Package, Clipboard, FileText, Mail, Copy, Check, Paperclip, GitMerge } from "lucide-react";

// §8ter journalisation : trace best-effort d'une action sensible sur un dossier.
async function logAudit(userId, entityId, action, details = {}) {
  try {
    await supabase.from("advisor_audit_log").insert({
      advisor_id: userId, actor_id: userId, client_entity_id: entityId || null, action, details,
    });
  } catch (e) { /* non bloquant */ }
}

const COPY = {
  fr: {
    title: "Dossiers clients", eyebrow: "Sans compte requis",
    subtitle: "Créez le dossier d’un client même s’il n’a pas encore de compte ARTKIV. Vous pourrez l’inviter plus tard ; ses collections, missions et devis restent rattachés au dossier.",
    add: "Ajouter un client", cancel: "Annuler",
    type: "Type de client", name: "Nom d’usage", namePh: "Ex. : Fondation Champ Libre, Famille Dubois…",
    email: "Email (facultatif)", emailPh: "email@exemple.com",
    create: "Créer le dossier", creating: "Création…",
    createdTitle: "Dossier client créé", createdBody: "Vous pouvez maintenant ajouter sa collection, préparer une mission ou l’inviter plus tard.",
    empty: "Aucun dossier pour le moment. Créez-en un pour commencer à suivre un client, même sans compte.",
    archive: "Archiver", archived: "Archivé",
    types: { individual: "Particulier", couple: "Couple", company: "Société", foundation: "Fondation", family: "Famille" },
    statuses: { prospect: "Prospect", active_unlinked: "Sans compte", invited: "Invitation envoyée", linked: "Relié à ARTKIV", archived: "Archivé" },
    nameRequired: "Le nom d’usage est requis.",
    createError: "Création impossible.",
    confirmArchive: "Archiver ce dossier client ?",
    authTitle: "Déclaration d’habilitation",
    authText: "Je confirme être autorisé à créer et gérer ce dossier pour le compte de mon client et à déposer les données et documents nécessaires à ma mission.",
    authCheckbox: "Je confirme cette déclaration",
    authRequired: "Vous devez confirmer la déclaration d’habilitation pour créer le dossier.",
    open: "Ouvrir", closeDetail: "Fermer",
    detailCollections: "Collections", detailMissions: "Missions", detailQuotes: "Devis",
    attach: "Rattacher", detach: "Détacher", noneAttached: "Rien de rattaché.",
    attachCollectionPh: "Rattacher une collection…", attachMissionPh: "Rattacher une mission…",
    noAttachable: "Aucun élément rattachable.", attachError: "Rattachement impossible (droits d’édition requis).",
    untitledCol: "Collection sans titre", untitledMission: "Mission",
    invite: "Inviter", inviteNoEmail: "Ajoutez un email au dossier pour pouvoir l’inviter.",
    inviteLinkTitle: "Lien d’invitation créé", inviteLinkHint: "Envoyez ce lien à votre client. Il expire dans 14 jours et lui demandera son accord avant tout rattachement.",
    copyLink: "Copier le lien", copied: "Copié", inviteError: "Invitation impossible.",
    resend: "Renvoyer le lien", revokeInvite: "Annuler l’invitation", confirmRevoke: "Annuler l’invitation en cours ?",
    filters: { all: "Tous", uninvited: "Non invités", pending: "En attente", linked: "Reliés" },
    fromCollection: "Créer un dossier depuis une collection", pickCollection: "Choisir une collection…", fromCollectionDone: "Dossier créé depuis la collection.",
    permissionsTitle: "Accès accordés", permissionsHint: "Aucune vue globale par défaut. Le client relié peut modifier ou révoquer.", declarative: "déclaratif",
    merge: "Fusionner", mergeBanner: (n) => `Fusionner « ${n} » vers un autre dossier`, mergePick: "Choisir le dossier cible…",
    mergeConfirm: "Les collections, missions et devis seront rattachés au dossier cible ; ce dossier sera archivé (aucune œuvre supprimée). Continuer ?",
    mergeDo: "Fusionner", mergeError: "Fusion impossible.",
    contribsTitle: "Contributions du client", validate: "Valider", reject: "Refuser", noContribs: "Aucune contribution.", openFile: "Ouvrir",
    cStatus: { submitted: "En attente", validated: "Validé", rejected: "Refusé" },
    requestsTitle: "Demandes de documents", newRequest: "Demander un document", requestTitlePh: "Ex. : Facture d’achat", requestDescPh: "Précisez ce que vous attendez…",
    sendRequest: "Demander", cancelReq: "Annuler la demande", noRequests: "Aucune demande.",
    rStatus: { requested: "En attente", fulfilled: "Fournie", cancelled: "Annulée" },
  },
  en: {
    title: "Client files", eyebrow: "No account required",
    subtitle: "Create a client’s file even if they don’t have an ARTKIV account yet. You can invite them later; their collections, missions and quotes stay attached to the file.",
    add: "Add a client", cancel: "Cancel",
    type: "Client type", name: "Display name", namePh: "E.g.: Champ Libre Foundation, Dubois Family…",
    email: "Email (optional)", emailPh: "email@example.com",
    create: "Create file", creating: "Creating…",
    createdTitle: "Client file created", createdBody: "You can now add their collection, prepare a mission or invite them later.",
    empty: "No file yet. Create one to start tracking a client, even without an account.",
    archive: "Archive", archived: "Archived",
    types: { individual: "Individual", couple: "Couple", company: "Company", foundation: "Foundation", family: "Family" },
    statuses: { prospect: "Prospect", active_unlinked: "No account", invited: "Invited", linked: "Linked to ARTKIV", archived: "Archived" },
    nameRequired: "Display name is required.",
    createError: "Could not create.",
    confirmArchive: "Archive this client file?",
    authTitle: "Authorisation declaration",
    authText: "I confirm that I am authorised to create and manage this file on behalf of my client and to upload the data and documents needed for my engagement.",
    authCheckbox: "I confirm this declaration",
    authRequired: "You must confirm the authorisation declaration to create the file.",
    open: "Open", closeDetail: "Close",
    detailCollections: "Collections", detailMissions: "Missions", detailQuotes: "Quotes",
    attach: "Attach", detach: "Detach", noneAttached: "Nothing attached.",
    attachCollectionPh: "Attach a collection…", attachMissionPh: "Attach a mission…",
    noAttachable: "Nothing to attach.", attachError: "Cannot attach (edit rights required).",
    untitledCol: "Untitled collection", untitledMission: "Mission",
    invite: "Invite", inviteNoEmail: "Add an email to the file to invite them.",
    inviteLinkTitle: "Invitation link created", inviteLinkHint: "Send this link to your client. It expires in 14 days and asks for their consent before any linking.",
    copyLink: "Copy link", copied: "Copied", inviteError: "Could not invite.",
    resend: "Resend link", revokeInvite: "Cancel invitation", confirmRevoke: "Cancel the pending invitation?",
    filters: { all: "All", uninvited: "Not invited", pending: "Pending", linked: "Linked" },
    fromCollection: "Create a file from a collection", pickCollection: "Pick a collection…", fromCollectionDone: "File created from the collection.",
    permissionsTitle: "Granted access", permissionsHint: "No global view by default. The linked client can change or revoke.", declarative: "declarative",
    merge: "Merge", mergeBanner: (n) => `Merge “${n}” into another file`, mergePick: "Pick target file…",
    mergeConfirm: "Collections, missions and quotes will move to the target file; this file will be archived (no artwork deleted). Continue?",
    mergeDo: "Merge", mergeError: "Merge failed.",
    contribsTitle: "Client contributions", validate: "Validate", reject: "Reject", noContribs: "No contribution.", openFile: "Open",
    cStatus: { submitted: "Pending", validated: "Validated", rejected: "Rejected" },
    requestsTitle: "Document requests", newRequest: "Request a document", requestTitlePh: "E.g.: Purchase invoice", requestDescPh: "Specify what you expect…",
    sendRequest: "Request", cancelReq: "Cancel request", noRequests: "No request.",
    rStatus: { requested: "Pending", fulfilled: "Provided", cancelled: "Cancelled" },
  },
  de: {
    title: "Kundenakten", eyebrow: "Kein Konto erforderlich",
    subtitle: "Legen Sie die Akte eines Kunden an, auch ohne ARTKIV-Konto. Sie können ihn später einladen; Sammlungen, Aufträge und Angebote bleiben mit der Akte verknüpft.",
    add: "Kunde hinzufügen", cancel: "Abbrechen",
    type: "Kundentyp", name: "Anzeigename", namePh: "Z. B.: Stiftung Champ Libre, Familie Dubois…",
    email: "E-Mail (optional)", emailPh: "email@beispiel.com",
    create: "Akte erstellen", creating: "Erstellen…",
    createdTitle: "Kundenakte erstellt", createdBody: "Sie können nun die Sammlung hinzufügen, einen Auftrag vorbereiten oder später einladen.",
    empty: "Noch keine Akte. Erstellen Sie eine, um einen Kunden auch ohne Konto zu betreuen.",
    archive: "Archivieren", archived: "Archiviert",
    types: { individual: "Privatperson", couple: "Paar", company: "Unternehmen", foundation: "Stiftung", family: "Familie" },
    statuses: { prospect: "Interessent", active_unlinked: "Ohne Konto", invited: "Eingeladen", linked: "Mit ARTKIV verknüpft", archived: "Archiviert" },
    nameRequired: "Anzeigename ist erforderlich.",
    createError: "Erstellen nicht möglich.",
    confirmArchive: "Diese Kundenakte archivieren?",
    authTitle: "Berechtigungserklärung",
    authText: "Ich bestätige, dass ich berechtigt bin, diese Akte im Auftrag meines Kunden anzulegen und zu verwalten und die für meinen Auftrag erforderlichen Daten und Dokumente hochzuladen.",
    authCheckbox: "Ich bestätige diese Erklärung",
    authRequired: "Sie müssen die Berechtigungserklärung bestätigen, um die Akte zu erstellen.",
    open: "Öffnen", closeDetail: "Schließen",
    detailCollections: "Sammlungen", detailMissions: "Aufträge", detailQuotes: "Angebote",
    attach: "Zuordnen", detach: "Lösen", noneAttached: "Nichts zugeordnet.",
    attachCollectionPh: "Sammlung zuordnen…", attachMissionPh: "Auftrag zuordnen…",
    noAttachable: "Nichts zuzuordnen.", attachError: "Zuordnung nicht möglich (Bearbeitungsrechte erforderlich).",
    untitledCol: "Sammlung ohne Titel", untitledMission: "Auftrag",
    invite: "Einladen", inviteNoEmail: "Fügen Sie der Akte eine E-Mail hinzu, um einzuladen.",
    inviteLinkTitle: "Einladungslink erstellt", inviteLinkHint: "Senden Sie diesen Link an Ihren Kunden. Er läuft in 14 Tagen ab und fragt vor jeder Verknüpfung um Zustimmung.",
    copyLink: "Link kopieren", copied: "Kopiert", inviteError: "Einladung nicht möglich.",
    resend: "Link erneut senden", revokeInvite: "Einladung stornieren", confirmRevoke: "Laufende Einladung stornieren?",
    filters: { all: "Alle", uninvited: "Nicht eingeladen", pending: "Ausstehend", linked: "Verknüpft" },
    fromCollection: "Akte aus Sammlung erstellen", pickCollection: "Sammlung wählen…", fromCollectionDone: "Akte aus der Sammlung erstellt.",
    permissionsTitle: "Gewährte Zugriffe", permissionsHint: "Keine globale Ansicht standardmäßig. Der verknüpfte Kunde kann ändern oder widerrufen.", declarative: "deklarativ",
    merge: "Zusammenführen", mergeBanner: (n) => `„${n}" in eine andere Akte zusammenführen`, mergePick: "Zielakte wählen…",
    mergeConfirm: "Sammlungen, Aufträge und Angebote werden in die Zielakte verschoben; diese Akte wird archiviert (kein Werk gelöscht). Fortfahren?",
    mergeDo: "Zusammenführen", mergeError: "Zusammenführung fehlgeschlagen.",
    contribsTitle: "Kundenbeiträge", validate: "Bestätigen", reject: "Ablehnen", noContribs: "Keine Beiträge.", openFile: "Öffnen",
    cStatus: { submitted: "Ausstehend", validated: "Bestätigt", rejected: "Abgelehnt" },
    requestsTitle: "Dokumentenanfragen", newRequest: "Dokument anfragen", requestTitlePh: "Z. B.: Kaufrechnung", requestDescPh: "Geben Sie an, was Sie erwarten…",
    sendRequest: "Anfragen", cancelReq: "Anfrage abbrechen", noRequests: "Keine Anfrage.",
    rStatus: { requested: "Ausstehend", fulfilled: "Geliefert", cancelled: "Abgebrochen" },
  },
};
const pick = (l) => COPY[String(l || "fr").slice(0, 2).toLowerCase()] || COPY.fr;

// Version du texte de déclaration d'habilitation (DPA §4.1) — horodatée à la création.
const AUTHORIZATION_VERSION = "2026-08-habilitation-v1";

const TYPE_ICON = { individual: User, couple: Users, company: Building2, foundation: Landmark, family: Home };
const TYPE_ORDER = ["individual", "couple", "family", "company", "foundation"];

const STATUS_STYLE = {
  active_unlinked: { bg: "#fbf6ea", color: "#8a6a2f" },
  prospect: { bg: "#eef1f6", color: "#3a4a6b" },
  invited: { bg: "#fbf6ea", color: "#8a6a2f" },
  linked: { bg: "#f1f6f1", color: "#2c5f2d" },
  archived: { bg: "#f5f5f4", color: "#57534e" },
};

const card = { background: "#fff", border: "1px solid #e5e3df", borderRadius: 2, padding: 20 };
const input = { width: "100%", padding: "10px 12px", border: "1px solid #e5e3df", borderRadius: 2, fontSize: 14, fontFamily: "system-ui, sans-serif", outline: "none" };

export default function AdvisorClientDossiers() {
  const { user } = useAuth() || {};
  const { i18n } = useTranslation();
  const tx = pick(i18n.resolvedLanguage || i18n.language);

  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justCreated, setJustCreated] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [inviteLink, setInviteLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pendingAttachCol, setPendingAttachCol] = useState(null);
  const [colPicker, setColPicker] = useState(null); // null | array of candidates
  const [mergeSource, setMergeSource] = useState(null); // dossier being merged from
  const [form, setForm] = useState({ entity_type: "individual", display_name: "", contact_email: "" });

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("advisor_client_entities")
      .select("*")
      .eq("primary_advisor_id", user.id)
      .neq("status", "archived")
      .order("created_at", { ascending: false });
    if (error) console.warn("[AdvisorClientDossiers] load", error);
    setDossiers(data || []);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const create = useCallback(async () => {
    if (!user?.id) return;
    const name = form.display_name.trim();
    if (!name) { alert(tx.nameRequired); return; }
    if (!authorized) { alert(tx.authRequired); return; }
    setSaving(true);
    try {
      const isCompany = form.entity_type === "company" || form.entity_type === "foundation";
      const { data: created, error } = await supabase.from("advisor_client_entities").insert({
        primary_advisor_id: user.id,
        created_by: user.id,
        entity_type: form.entity_type,
        display_name: name,
        company_name: isCompany ? name : null,
        contact_email: form.contact_email.trim() || null,
        status: "active_unlinked",
        authorization_confirmed_at: new Date().toISOString(),
        authorization_version: AUTHORIZATION_VERSION,
      }).select("id").single();
      if (error) throw error;
      await logAudit(user.id, created?.id, AUDIT_ACTIONS.DOSSIER_CREATED, { entity_type: form.entity_type });
      // Migration : rattacher la collection d'origine si le dossier vient d'une collection.
      if (pendingAttachCol && created?.id) {
        const { error: attErr } = await supabase.from("collections")
          .update({ client_entity_id: created.id, managed_by_advisor_id: user.id, management_status: "advisor_managed" })
          .eq("id", pendingAttachCol);
        if (!attErr) await logAudit(user.id, created.id, AUDIT_ACTIONS.COLLECTION_ATTACHED, { collection_id: pendingAttachCol, via: "migration" });
      }
      setPendingAttachCol(null);
      setForm({ entity_type: "individual", display_name: "", contact_email: "" });
      setAuthorized(false);
      setShowForm(false);
      setJustCreated(true);
      setTimeout(() => setJustCreated(false), 6000);
      await load();
    } catch (e) {
      console.warn("[AdvisorClientDossiers] create", e);
      alert(`${tx.createError} ${e?.message || ""}`);
    } finally {
      setSaving(false);
    }
  }, [user?.id, form, tx, load, authorized, pendingAttachCol]);

  const archive = useCallback(async (id) => {
    if (!window.confirm(tx.confirmArchive)) return;
    setDossiers((prev) => prev.filter((d) => d.id !== id));
    await supabase.from("advisor_client_entities").update({ status: "archived" }).eq("id", id);
    await logAudit(user?.id, id, AUDIT_ACTIONS.DOSSIER_ARCHIVED);
  }, [tx, user?.id]);

  const invite = useCallback(async (dossier) => {
    if (!user?.id) return;
    const email = (dossier.contact_email || "").trim();
    if (!email) { alert(tx.inviteNoEmail); return; }
    const token = (window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)) +
                  (window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
    try {
      // Renvoi : on révoque les invitations en attente existantes (un seul lien vivant).
      await supabase.from("advisor_client_invitations")
        .update({ status: "revoked", updated_at: new Date().toISOString() })
        .eq("client_entity_id", dossier.id).eq("status", "pending");
      const { error } = await supabase.from("advisor_client_invitations").insert({
        client_entity_id: dossier.id,
        advisor_id: user.id,
        invited_email: email,
        token,
        created_by: user.id,
      });
      if (error) throw error;
      await supabase.from("advisor_client_entities").update({ status: "invited" }).eq("id", dossier.id);
      // Journal d'audit (§8ter)
      await supabase.from("advisor_audit_log").insert({
        advisor_id: user.id, actor_id: user.id, client_entity_id: dossier.id,
        action: "invitation_created", details: { invited_email: email },
      });
      setInviteLink(`${window.location.origin}/client-link/${token}`);
      setCopied(false);
      await load();
    } catch (e) {
      console.warn("[AdvisorClientDossiers] invite", e);
      alert(`${tx.inviteError} ${e?.message || ""}`);
    }
  }, [user?.id, tx, load]);

  const copyInvite = useCallback(async () => {
    if (!inviteLink) return;
    try { await navigator.clipboard.writeText(inviteLink); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch {}
  }, [inviteLink]);

  // Migration (§8bis) : proposer les collections gérées non encore rattachées à un dossier.
  const openColPicker = useCallback(async () => {
    if (colPicker) { setColPicker(null); return; }
    if (!user?.id) return;
    const { data: perms } = await supabase
      .from("collection_permissions").select("collection_id, client_id, can_edit")
      .eq("grantee_id", user.id).eq("can_edit", true);
    const editColIds = new Set((perms || []).filter((p) => p.collection_id).map((p) => p.collection_id));
    const editClientIds = new Set((perms || []).filter((p) => !p.collection_id && p.client_id).map((p) => p.client_id));
    const { data: cols } = await supabase.from("collections").select("id, title, client_id, client_entity_id").is("client_entity_id", null);
    const cands = (cols || []).filter((c) => c.client_id === user.id || editColIds.has(c.id) || editClientIds.has(c.client_id));
    setColPicker(cands);
  }, [colPicker, user?.id]);

  // Sélection d'une collection → préremplit le formulaire (déclaration d'habilitation
  // toujours requise) et attachera la collection après création.
  const pickCollectionForDossier = useCallback((col) => {
    setForm({ entity_type: "individual", display_name: col.title || "", contact_email: "" });
    setPendingAttachCol(col.id);
    setColPicker(null);
    setShowForm(true);
  }, []);

  const doMerge = useCallback(async (targetId) => {
    if (!mergeSource || !targetId) return;
    if (!window.confirm(tx.mergeConfirm)) return;
    const { data, error } = await supabase.rpc("merge_client_dossiers", { p_source: mergeSource.id, p_target: targetId });
    if (error || data?.error) { alert(`${tx.mergeError} ${data?.error || error?.message || ""}`); return; }
    setMergeSource(null);
    if (openId === mergeSource.id) setOpenId(null);
    await load();
  }, [mergeSource, tx, load, openId]);

  const revoke = useCallback(async (dossier) => {
    if (!user?.id || !window.confirm(tx.confirmRevoke)) return;
    await supabase.from("advisor_client_invitations")
      .update({ status: "revoked", updated_at: new Date().toISOString() })
      .eq("client_entity_id", dossier.id).eq("status", "pending");
    await supabase.from("advisor_client_entities")
      .update({ status: "active_unlinked" }).eq("id", dossier.id).eq("status", "invited");
    setInviteLink(null);
    await load();
  }, [user?.id, tx, load]);

  const count = dossiers.length;
  const activeTypes = useMemo(() => TYPE_ORDER, []);
  const filtered = useMemo(() => dossiers.filter((d) => {
    if (filter === "uninvited") return d.status === "prospect" || d.status === "active_unlinked";
    if (filter === "pending") return d.status === "invited";
    if (filter === "linked") return d.status === "linked";
    return true;
  }), [dossiers, filter]);

  return (
    <div style={{ ...card, marginBottom: 24, fontFamily: 'Georgia, "Times New Roman", serif' }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ maxWidth: 640 }}>
          <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.09em", color: "#8b7355", fontFamily: "system-ui, sans-serif", fontWeight: 600, marginBottom: 4 }}>{tx.eyebrow}</div>
          <h2 style={{ fontSize: 22, fontWeight: 400, color: "#1a1a1a", margin: 0, letterSpacing: "-0.02em" }}>
            {tx.title}{count > 0 ? ` · ${count}` : ""}
          </h2>
          <p style={{ marginTop: 6, marginBottom: 0, fontSize: 13, color: "#6b6b6b", fontFamily: "system-ui, sans-serif", lineHeight: 1.6 }}>{tx.subtitle}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => { if (v) setAuthorized(false); return !v; })}
          style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 16px", background: showForm ? "#fff" : "#1a1a1a", color: showForm ? "#1a1a1a" : "#fff", border: showForm ? "1px solid #e5e3df" : "none", borderRadius: 2, fontFamily: "system-ui, sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
        >
          <UserPlus size={16} /> {showForm ? tx.cancel : tx.add}
        </button>
      </div>

      {/* Migration : créer un dossier depuis une collection existante gérée */}
      <div style={{ marginBottom: 12 }}>
        <button type="button" onClick={openColPicker}
          style={{ background: "transparent", border: "none", color: "#8b7355", fontFamily: "system-ui, sans-serif", fontSize: 12.5, fontWeight: 600, cursor: "pointer", padding: 0 }}>
          {tx.fromCollection}
        </button>
        {Array.isArray(colPicker) && (
          <select defaultValue="" onChange={(e) => { const c = colPicker.find((x) => x.id === e.target.value); if (c) pickCollectionForDossier(c); }}
            style={{ ...input, marginTop: 8, maxWidth: 360, cursor: "pointer" }}>
            <option value="">{tx.pickCollection}</option>
            {colPicker.map((c) => <option key={c.id} value={c.id}>{c.title || tx.untitledCol}</option>)}
          </select>
        )}
      </div>

      {justCreated && (
        <div style={{ marginBottom: 14, padding: "12px 14px", background: "#f1f6f1", border: "1px solid #cfe3cf", borderRadius: 2 }}>
          <div style={{ fontSize: 14, color: "#1a1a1a", fontWeight: 600, fontFamily: "system-ui, sans-serif" }}>{tx.createdTitle}</div>
          <div style={{ fontSize: 13, color: "#2c5f2d", fontFamily: "system-ui, sans-serif", marginTop: 2 }}>{tx.createdBody}</div>
        </div>
      )}

      {mergeSource && (
        <div style={{ marginBottom: 14, padding: "12px 14px", background: "#fbf6ea", border: "1px solid #ecdcae", borderRadius: 2, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "#1a1a1a", fontFamily: "system-ui, sans-serif", fontWeight: 600 }}>{tx.mergeBanner(mergeSource.display_name)}</span>
          <select defaultValue="" onChange={(e) => e.target.value && doMerge(e.target.value)} style={{ ...input, maxWidth: 280, cursor: "pointer" }}>
            <option value="">{tx.mergePick}</option>
            {dossiers.filter((d) => d.id !== mergeSource.id).map((d) => <option key={d.id} value={d.id}>{d.display_name}</option>)}
          </select>
          <button type="button" onClick={() => setMergeSource(null)} style={{ background: "transparent", border: "none", color: "#8a6a2f", fontFamily: "system-ui, sans-serif", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>{tx.cancel}</button>
        </div>
      )}

      {inviteLink && (
        <div style={{ marginBottom: 14, padding: "12px 14px", background: "#eef1f6", border: "1px solid #d3dbe8", borderRadius: 2 }}>
          <div style={{ fontSize: 14, color: "#1a1a1a", fontWeight: 600, fontFamily: "system-ui, sans-serif" }}>{tx.inviteLinkTitle}</div>
          <div style={{ fontSize: 12.5, color: "#3a4a6b", fontFamily: "system-ui, sans-serif", margin: "4px 0 10px", lineHeight: 1.5 }}>{tx.inviteLinkHint}</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input readOnly value={inviteLink} onFocus={(e) => e.target.select()} style={{ flex: 1, minWidth: 220, ...input, fontSize: 12.5, background: "#fff" }} />
            <button type="button" onClick={copyInvite}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 2, fontFamily: "system-ui, sans-serif", fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? tx.copied : tx.copyLink}
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <div style={{ marginBottom: 16, padding: 16, background: "#fafaf8", border: "1px solid #e5e3df", borderRadius: 2 }}>
          <div style={{ display: "grid", gap: 14 }}>
            <div>
              <label style={lbl}>{tx.type}</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {activeTypes.map((tp) => {
                  const Icon = TYPE_ICON[tp];
                  const on = form.entity_type === tp;
                  return (
                    <button key={tp} type="button" onClick={() => set("entity_type", tp)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 2, cursor: "pointer", fontFamily: "system-ui, sans-serif", fontSize: 13, fontWeight: on ? 700 : 400,
                        border: `1px solid ${on ? "#1a1a1a" : "#e5e3df"}`, background: on ? "#1a1a1a" : "#fff", color: on ? "#fff" : "#6b6b6b" }}>
                      <Icon size={14} /> {tx.types[tp]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label style={lbl}>{tx.name}</label>
              <input style={input} value={form.display_name} onChange={(e) => set("display_name", e.target.value)} placeholder={tx.namePh} autoFocus />
            </div>
            <div>
              <label style={lbl}>{tx.email}</label>
              <input style={input} type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder={tx.emailPh} />
            </div>
            {/* Déclaration d'habilitation obligatoire (DPA §4.1) */}
            <div style={{ padding: "12px 14px", background: "#fbf6ea", border: "1px solid #ecdcae", borderRadius: 2 }}>
              <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8b7355", fontFamily: "system-ui, sans-serif", fontWeight: 700, marginBottom: 6 }}>{tx.authTitle}</div>
              <label style={{ display: "flex", gap: 9, alignItems: "flex-start", cursor: "pointer", fontFamily: "system-ui, sans-serif" }}>
                <input type="checkbox" checked={authorized} onChange={(e) => setAuthorized(e.target.checked)} style={{ marginTop: 3, width: 15, height: 15, accentColor: "#8b7355", flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, color: "#4a4a4a", lineHeight: 1.5 }}>{tx.authText}</span>
              </label>
            </div>
            <div>
              <button type="button" disabled={saving || !authorized} onClick={create}
                style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 18px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 2, fontFamily: "system-ui, sans-serif", fontSize: 14, fontWeight: 700, cursor: (saving || !authorized) ? "not-allowed" : "pointer", opacity: (saving || !authorized) ? 0.55 : 1 }}>
                <FolderPlus size={16} /> {saving ? tx.creating : tx.create}
              </button>
            </div>
          </div>
        </div>
      )}

      {dossiers.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {["all", "uninvited", "pending", "linked"].map((f) => (
            <button key={f} type="button" onClick={() => setFilter(f)}
              style={{ padding: "5px 11px", borderRadius: 999, fontFamily: "system-ui, sans-serif", fontSize: 12, fontWeight: filter === f ? 700 : 500, cursor: "pointer",
                border: `1px solid ${filter === f ? "#1a1a1a" : "#e5e3df"}`, background: filter === f ? "#1a1a1a" : "#fff", color: filter === f ? "#fff" : "#6b6b6b" }}>
              {tx.filters[f]}
            </button>
          ))}
        </div>
      )}

      {loading ? null : dossiers.length === 0 && !showForm ? (
        <div style={{ padding: "16px", border: "1px dashed #e5e3df", borderRadius: 2, background: "#fafaf8", fontFamily: "system-ui, sans-serif", fontSize: 13, color: "#6b6b6b" }}>{tx.empty}</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(260px, 100%), 1fr))", gap: 10 }}>
          {filtered.map((d) => {
            const Icon = TYPE_ICON[d.entity_type] || User;
            const st = STATUS_STYLE[d.status] || STATUS_STYLE.active_unlinked;
            return (
              <div key={d.id} style={{ border: "1px solid #e5e3df", borderRadius: 2, padding: 14, background: "#fff", display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 32, height: 32, borderRadius: 2, background: "#faf6ea", color: "#8b7355", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon size={16} /></span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 14.5, color: "#1a1a1a", fontFamily: "Georgia, serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.display_name}</div>
                    <div style={{ fontSize: 11.5, color: "#9ca3af", fontFamily: "system-ui, sans-serif" }}>{tx.types[d.entity_type] || d.entity_type}</div>
                  </div>
                </div>
                {d.contact_email && <div style={{ fontSize: 12, color: "#6b6b6b", fontFamily: "system-ui, sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.contact_email}</div>}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 2 }}>
                  <span style={{ fontSize: 10.5, fontFamily: "system-ui, sans-serif", fontWeight: 600, padding: "3px 8px", borderRadius: 999, background: st.bg, color: st.color }}>{tx.statuses[d.status] || d.status}</span>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    {["prospect", "active_unlinked"].includes(d.status) && (
                      <button type="button" onClick={() => invite(d)} title={d.contact_email ? tx.invite : tx.inviteNoEmail}
                        style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "transparent", border: "none", color: d.contact_email ? "#3a4a6b" : "#c0bcb2", fontFamily: "system-ui, sans-serif", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
                        <Mail size={13} /> {tx.invite}
                      </button>
                    )}
                    {d.status === "invited" && (
                      <>
                        <button type="button" onClick={() => invite(d)} title={tx.resend}
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "transparent", border: "none", color: "#3a4a6b", fontFamily: "system-ui, sans-serif", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
                          <Mail size={13} /> {tx.resend}
                        </button>
                        <button type="button" onClick={() => revoke(d)} title={tx.revokeInvite}
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "transparent", border: "none", color: "#9ca3af", fontFamily: "system-ui, sans-serif", fontSize: 11.5, cursor: "pointer" }}>
                          {tx.revokeInvite}
                        </button>
                      </>
                    )}
                    <button type="button" onClick={() => setOpenId((v) => (v === d.id ? null : d.id))}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "transparent", border: "none", color: "#8b7355", fontFamily: "system-ui, sans-serif", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
                      {openId === d.id ? tx.closeDetail : tx.open}
                      <ChevronDown size={13} style={{ transform: openId === d.id ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
                    </button>
                    {dossiers.length > 1 && (
                      <button type="button" onClick={() => setMergeSource(d)} title={tx.merge}
                        style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "transparent", border: "none", color: "#8b7355", fontFamily: "system-ui, sans-serif", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
                        <GitMerge size={13} /> {tx.merge}
                      </button>
                    )}
                    <button type="button" onClick={() => archive(d.id)} title={tx.archive}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "transparent", border: "none", color: "#9ca3af", fontFamily: "system-ui, sans-serif", fontSize: 11.5, cursor: "pointer" }}>
                      <Archive size={13} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {openId && dossiers.some((d) => d.id === openId) && (
        <div style={{ marginTop: 12 }}>
          <DossierDetail dossier={dossiers.find((d) => d.id === openId)} userId={user?.id} tx={tx} lang={i18n.resolvedLanguage || i18n.language} onPermsChanged={load} />
        </div>
      )}
    </div>
  );
}

const lbl = { display: "block", fontSize: 11, color: "#6b6b6b", marginBottom: 6, fontFamily: "system-ui, sans-serif", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 };

// ── Détail d'un dossier : rattacher/détacher collections & missions, compter les devis ──
function DossierDetail({ dossier, userId, tx, lang, onPermsChanged }) {
  const [grants, setGrants] = useState(() => mergeGrants(dossier?.granted_permissions));
  const [permsBusy, setPermsBusy] = useState(false);
  const [attCols, setAttCols] = useState([]);
  const [attMissions, setAttMissions] = useState([]);
  const [candCols, setCandCols] = useState([]);
  const [candMissions, setCandMissions] = useState([]);
  const [quoteCount, setQuoteCount] = useState(0);
  const [contribs, setContribs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [reqForm, setReqForm] = useState({ title: "", description: "" });
  const [showReqForm, setShowReqForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId || !dossier?.id) return;
    setLoading(true);
    // Périmètre éditable de l'advisor sur les collections (can_edit) pour ne proposer que l'attachable
    const { data: perms } = await supabase
      .from("collection_permissions")
      .select("collection_id, client_id, can_edit")
      .eq("grantee_id", userId)
      .eq("can_edit", true);
    const editColIds = new Set((perms || []).filter((p) => p.collection_id).map((p) => p.collection_id));
    const editClientIds = new Set((perms || []).filter((p) => !p.collection_id && p.client_id).map((p) => p.client_id));
    const canEdit = (c) => c.client_id === userId || editColIds.has(c.id) || editClientIds.has(c.client_id);

    const [{ data: cols }, { data: missions }, { count: qc }] = await Promise.all([
      supabase.from("collections").select("id, title, client_id, client_entity_id").or(`client_entity_id.eq.${dossier.id},client_entity_id.is.null`),
      supabase.from("advisor_missions").select("id, title, mission_type, client_entity_id").eq("advisor_id", userId).or(`client_entity_id.eq.${dossier.id},client_entity_id.is.null`),
      supabase.from("advisor_quotes").select("id", { count: "exact", head: true }).eq("client_entity_id", dossier.id),
    ]);
    const allCols = cols || [];
    setAttCols(allCols.filter((c) => c.client_entity_id === dossier.id));
    setCandCols(allCols.filter((c) => c.client_entity_id == null && canEdit(c)));
    const allM = missions || [];
    setAttMissions(allM.filter((m) => m.client_entity_id === dossier.id));
    setCandMissions(allM.filter((m) => m.client_entity_id == null));
    setQuoteCount(qc || 0);

    const [{ data: cc }, { data: rq }] = await Promise.all([
      supabase.from("advisor_client_contributions").select("*").eq("client_entity_id", dossier.id).order("created_at", { ascending: false }),
      supabase.from("advisor_client_requests").select("*").eq("client_entity_id", dossier.id).order("created_at", { ascending: false }),
    ]);
    setContribs(cc || []);
    setRequests(rq || []);
    setLoading(false);
  }, [userId, dossier?.id]);

  const createRequest = async () => {
    const title = reqForm.title.trim();
    if (!title) return;
    const { error } = await supabase.from("advisor_client_requests").insert({
      client_entity_id: dossier.id, advisor_id: dossier.primary_advisor_id || userId,
      title, description: reqForm.description.trim() || null, created_by: userId,
    });
    if (error) { alert(error.message); return; }
    setReqForm({ title: "", description: "" });
    setShowReqForm(false);
    load();
  };
  const cancelRequest = async (id) => {
    await supabase.from("advisor_client_requests").update({ status: "cancelled", updated_at: new Date().toISOString() }).eq("id", id);
    load();
  };

  const reviewContrib = async (id, status) => {
    await supabase.from("advisor_client_contributions")
      .update({ status, reviewed_by: userId, reviewed_at: new Date().toISOString() }).eq("id", id);
    load();
  };
  const openContribFile = async (path) => {
    if (!path) return;
    const { data } = await supabase.storage.from("client-contributions").createSignedUrl(path, 300);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
  };

  useEffect(() => { load(); }, [load]);

  const attachCol = async (id) => {
    const { error } = await supabase.from("collections")
      .update({ client_entity_id: dossier.id, managed_by_advisor_id: userId, management_status: "advisor_managed" })
      .eq("id", id);
    if (error) { alert(tx.attachError); return; }
    await logAudit(userId, dossier.id, AUDIT_ACTIONS.COLLECTION_ATTACHED, { collection_id: id });
    load();
  };
  const detachCol = async (id) => {
    await supabase.from("collections").update({ client_entity_id: null }).eq("id", id);
    await logAudit(userId, dossier.id, AUDIT_ACTIONS.COLLECTION_DETACHED, { collection_id: id });
    load();
  };
  const attachMission = async (id) => {
    const { error } = await supabase.from("advisor_missions").update({ client_entity_id: dossier.id }).eq("id", id);
    if (error) { alert(tx.attachError); return; }
    await logAudit(userId, dossier.id, AUDIT_ACTIONS.MISSION_ATTACHED, { mission_id: id });
    load();
  };
  const detachMission = async (id) => {
    await supabase.from("advisor_missions").update({ client_entity_id: null }).eq("id", id);
    await logAudit(userId, dossier.id, AUDIT_ACTIONS.MISSION_DETACHED, { mission_id: id });
    load();
  };

  const togglePerm = async (key) => {
    const next = { ...grants, [key]: !grants[key] };
    setGrants(next);
    setPermsBusy(true);
    const { data, error } = await supabase.rpc("set_dossier_permissions", { p_entity_id: dossier.id, p_permissions: next });
    setPermsBusy(false);
    if (error || data?.error) { setGrants(grants); return; } // rollback en cas d'échec
    onPermsChanged?.();
  };

  const box = { background: "#fff", border: "1px solid #e5e3df", borderRadius: 2, padding: 14 };
  const attSel = { padding: "7px 10px", border: "1px solid #e5e3df", borderRadius: 2, fontSize: 12.5, fontFamily: "system-ui, sans-serif", background: "#fff", cursor: "pointer", maxWidth: "100%" };
  const row = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 0", borderBottom: "1px solid #f5f5f4", fontFamily: "system-ui, sans-serif", fontSize: 13 };

  return (
    <div style={{ background: "#fafaf8", border: "1px solid #e5e3df", borderRadius: 2, padding: 16 }}>
      {loading ? (
        <div style={{ color: "#9ca3af", fontFamily: "system-ui, sans-serif", fontSize: 13 }}>…</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: 12 }}>
          {/* Collections */}
          <div style={box}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8b7355", fontFamily: "system-ui, sans-serif", fontWeight: 700, marginBottom: 8 }}>
              <Package size={13} /> {tx.detailCollections} · {attCols.length}
            </div>
            {attCols.length === 0 ? <div style={{ fontSize: 12.5, color: "#9ca3af", fontFamily: "system-ui, sans-serif", marginBottom: 8 }}>{tx.noneAttached}</div> :
              attCols.map((c) => (
                <div key={c.id} style={row}>
                  <span style={{ color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.title || tx.untitledCol}</span>
                  <button type="button" onClick={() => detachCol(c.id)} title={tx.detach} style={{ background: "transparent", border: "none", color: "#9ca3af", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11.5, fontFamily: "system-ui, sans-serif", flexShrink: 0 }}><Unlink size={13} /></button>
                </div>
              ))}
            {candCols.length > 0 ? (
              <select value="" onChange={(e) => e.target.value && attachCol(e.target.value)} style={{ ...attSel, marginTop: 8, width: "100%" }}>
                <option value="">{tx.attachCollectionPh}</option>
                {candCols.map((c) => <option key={c.id} value={c.id}>{c.title || tx.untitledCol}</option>)}
              </select>
            ) : <div style={{ fontSize: 11.5, color: "#c0bcb2", fontFamily: "system-ui, sans-serif", marginTop: 8 }}>{tx.noAttachable}</div>}
          </div>

          {/* Missions */}
          <div style={box}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8b7355", fontFamily: "system-ui, sans-serif", fontWeight: 700, marginBottom: 8 }}>
              <Clipboard size={13} /> {tx.detailMissions} · {attMissions.length}
            </div>
            {attMissions.length === 0 ? <div style={{ fontSize: 12.5, color: "#9ca3af", fontFamily: "system-ui, sans-serif", marginBottom: 8 }}>{tx.noneAttached}</div> :
              attMissions.map((m) => (
                <div key={m.id} style={row}>
                  <span style={{ color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.title || tx.untitledMission}</span>
                  <button type="button" onClick={() => detachMission(m.id)} title={tx.detach} style={{ background: "transparent", border: "none", color: "#9ca3af", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11.5, fontFamily: "system-ui, sans-serif", flexShrink: 0 }}><Unlink size={13} /></button>
                </div>
              ))}
            {candMissions.length > 0 ? (
              <select value="" onChange={(e) => e.target.value && attachMission(e.target.value)} style={{ ...attSel, marginTop: 8, width: "100%" }}>
                <option value="">{tx.attachMissionPh}</option>
                {candMissions.map((m) => <option key={m.id} value={m.id}>{m.title || tx.untitledMission}</option>)}
              </select>
            ) : <div style={{ fontSize: 11.5, color: "#c0bcb2", fontFamily: "system-ui, sans-serif", marginTop: 8 }}>{tx.noAttachable}</div>}
          </div>

          {/* Devis (compteur) */}
          <div style={box}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8b7355", fontFamily: "system-ui, sans-serif", fontWeight: 700, marginBottom: 8 }}>
              <FileText size={13} /> {tx.detailQuotes} · {quoteCount}
            </div>
            <div style={{ fontSize: 12.5, color: "#9ca3af", fontFamily: "system-ui, sans-serif" }}>
              {quoteCount === 0 ? tx.noneAttached : ""}
            </div>
          </div>

          {/* Permissions granulaires (§8ter) */}
          <div style={{ ...box, gridColumn: "1 / -1" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8b7355", fontFamily: "system-ui, sans-serif", fontWeight: 700, marginBottom: 4 }}>
              {tx.permissionsTitle}
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af", fontFamily: "system-ui, sans-serif", marginBottom: 10, lineHeight: 1.5 }}>{tx.permissionsHint}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: 6 }}>
              {PERMISSION_CATEGORIES.map((cat) => (
                <label key={cat.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 9px", border: "1px solid #e5e3df", borderRadius: 2, cursor: permsBusy ? "default" : "pointer", background: grants[cat.key] ? "#faf6ea" : "#fff", opacity: permsBusy ? 0.7 : 1 }}>
                  <input type="checkbox" checked={!!grants[cat.key]} disabled={permsBusy} onChange={() => togglePerm(cat.key)} style={{ width: 15, height: 15, accentColor: "#8b7355", flexShrink: 0 }} />
                  <span style={{ fontSize: 12.5, color: "#1a1a1a", fontFamily: "system-ui, sans-serif" }}>{permissionLabel(cat.key, lang)}</span>
                  {!cat.enforcedServerSide && <span style={{ marginLeft: "auto", fontSize: 9.5, color: "#c0bcb2", fontFamily: "system-ui, sans-serif", textTransform: "uppercase", letterSpacing: "0.04em", flexShrink: 0 }}>{tx.declarative}</span>}
                </label>
              ))}
            </div>
          </div>

          {/* Demandes de documents (§8bis) */}
          <div style={{ ...box, gridColumn: "1 / -1" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8b7355", fontFamily: "system-ui, sans-serif", fontWeight: 700 }}>
                <Mail size={13} /> {tx.requestsTitle} · {requests.filter((r) => r.status === "requested").length}
              </div>
              <button type="button" onClick={() => { setShowReqForm((v) => !v); setReqForm({ title: "", description: "" }); }}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "transparent", border: "none", color: "#3a4a6b", fontFamily: "system-ui, sans-serif", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                <FolderPlus size={13} /> {showReqForm ? tx.cancelReq : tx.newRequest}
              </button>
            </div>
            {showReqForm && (
              <div style={{ marginBottom: 10, padding: 12, background: "#fafaf8", border: "1px solid #e5e3df", borderRadius: 2, display: "grid", gap: 8 }}>
                <input value={reqForm.title} onChange={(e) => setReqForm((p) => ({ ...p, title: e.target.value }))} placeholder={tx.requestTitlePh}
                  style={{ padding: "8px 10px", border: "1px solid #e5e3df", borderRadius: 2, fontSize: 13, fontFamily: "system-ui, sans-serif", outline: "none" }} />
                <textarea value={reqForm.description} onChange={(e) => setReqForm((p) => ({ ...p, description: e.target.value }))} placeholder={tx.requestDescPh} rows={2}
                  style={{ padding: "8px 10px", border: "1px solid #e5e3df", borderRadius: 2, fontSize: 13, fontFamily: "system-ui, sans-serif", outline: "none", resize: "vertical" }} />
                <div>
                  <button type="button" onClick={createRequest}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 2, fontFamily: "system-ui, sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                    <Mail size={14} /> {tx.sendRequest}
                  </button>
                </div>
              </div>
            )}
            {requests.length === 0 ? (
              <div style={{ fontSize: 12.5, color: "#9ca3af", fontFamily: "system-ui, sans-serif" }}>{tx.noRequests}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {requests.map((r) => {
                  const tone = r.status === "fulfilled" ? { bg: "#f1f6f1", cc: "#2c5f2d" } : r.status === "cancelled" ? { bg: "#f5f5f4", cc: "#57534e" } : { bg: "#fbf6ea", cc: "#8a6a2f" };
                  return (
                    <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 10px", border: "1px solid #e5e3df", borderRadius: 2, fontFamily: "system-ui, sans-serif" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: "#1a1a1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.title}</div>
                        {r.description && <div style={{ fontSize: 11.5, color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.description}</div>}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                        {r.status === "requested" && (
                          <button type="button" onClick={() => cancelRequest(r.id)} style={{ background: "transparent", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 11.5 }}>{tx.cancelReq}</button>
                        )}
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 999, background: tone.bg, color: tone.cc }}>{tx.rStatus[r.status] || r.status}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Contributions du client (§8bis) */}
          <div style={{ ...box, gridColumn: "1 / -1" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8b7355", fontFamily: "system-ui, sans-serif", fontWeight: 700, marginBottom: 8 }}>
              <FileText size={13} /> {tx.contribsTitle} · {contribs.length}
            </div>
            {contribs.length === 0 ? (
              <div style={{ fontSize: 12.5, color: "#9ca3af", fontFamily: "system-ui, sans-serif" }}>{tx.noContribs}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {contribs.map((c) => {
                  const tone = c.status === "validated" ? { bg: "#f1f6f1", cc: "#2c5f2d" } : c.status === "rejected" ? { bg: "#f8eef0", cc: "#8f3a44" } : { bg: "#fbf6ea", cc: "#8a6a2f" };
                  return (
                    <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 10px", border: "1px solid #e5e3df", borderRadius: 2, fontFamily: "system-ui, sans-serif" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: "#1a1a1a", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {c.kind === "document" && <FileText size={12} style={{ flexShrink: 0, color: "#8b7355" }} />}{c.title || c.file_name || "—"}
                        </div>
                        {c.note && <div style={{ fontSize: 11.5, color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.note}</div>}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                        {c.storage_path && (
                          <button type="button" onClick={() => openContribFile(c.storage_path)} title={tx.openFile}
                            style={{ background: "transparent", border: "none", color: "#8b7355", cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
                            <Paperclip size={14} />
                          </button>
                        )}
                        {c.status === "submitted" ? (
                          <>
                            <button type="button" onClick={() => reviewContrib(c.id, "validated")} style={{ background: "transparent", border: "none", color: "#2c5f2d", cursor: "pointer", fontSize: 11.5, fontWeight: 600 }}>{tx.validate}</button>
                            <button type="button" onClick={() => reviewContrib(c.id, "rejected")} style={{ background: "transparent", border: "none", color: "#8f3a44", cursor: "pointer", fontSize: 11.5, fontWeight: 600 }}>{tx.reject}</button>
                          </>
                        ) : (
                          <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 999, background: tone.bg, color: tone.cc }}>{tx.cStatus[c.status] || c.status}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
